import React from 'react';
import { ITextProps, Text, Link, ILinkProps } from '@fluentui/react';
import { useReactiveVar } from '@apollo/client';
import { useRouteMatch, useHistory } from 'react-router-dom';
import { highlightTextMode, setUserDefaults } from 'utility/cache/ui';
import { useStyles } from './index.styles';
import clsx from 'clsx';

type HighlightTextViewProps = ITextProps &
  ILinkProps & {
    preventDefault?: boolean;
    isHighlightable?: boolean;
    highlightText: string;
    text: string;
    to?: string;
    isSelectedDocument?: boolean;
    navigationState?: any;
    onLinkClick?: () => void;
  };

export const HighLightActiveLink: React.FC<HighlightTextViewProps> = ({
  isHighlightable,
  highlightText,
  text,
  to,
  preventDefault,
  navigationState,
  onLinkClick,
  isSelectedDocument,
  ...props
}) => {
  const escapedHighlightText = highlightText
    ? highlightText.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    : '';
  const parts = escapedHighlightText
    ? text?.split(new RegExp(`(${escapedHighlightText})`, 'gi'))
    : [text];

  const userDefaults = useReactiveVar(setUserDefaults);
  const isRowSelectorBold = Boolean(userDefaults?.isRowSelectorBold);
  const highlightTextState = useReactiveVar(highlightTextMode);
  const history = useHistory();

  // Safely check route match only when `to` is defined, fallback to false
  const routeMatch = useRouteMatch(to ?? '/__no_route__');
  const match = isSelectedDocument ?? Boolean(routeMatch);

  const styles = useStyles();

  // Selected row is bold when isRowSelectorBold is false; regular when isRowSelectorBold is true
  const shouldBeBold = isRowSelectorBold ? !match : match;
  const linkClass = clsx(shouldBeBold ? styles.linkBold : styles.link);

  const highlightStyle = { backgroundColor: 'rgb(235,157,56)' };

  return (
    <Link
      className={styles.root}
      onClick={() => {
        if (preventDefault) {
          onLinkClick?.();
        } else if (to) {
          history.push(to, {
            ...navigationState,
          });
        }
      }}
      {...props}
    >
      <Text variant="xLarge">
        {highlightTextState && highlightText ? (
          parts?.map((part, index) => {
            const isHighlighted =
              part.toLowerCase() === highlightText.toLowerCase();

            return (
              <Text
                key={index}
                style={isHighlighted ? highlightStyle : undefined}
                className={linkClass}
              >
                {part}
              </Text>
            );
          })
        ) : (
          <Text className={linkClass}>{text}</Text>
        )}
      </Text>
    </Link>
  );
};